import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { AlertTriangle, MoreHorizontal, Power, Ticket, Trash2 } from 'lucide-react';

import { usePromotion, useUpdatePromotion, PromotionDetails } from '@/lib/api/promotions';
import { useAccountTimezone } from '@/hooks/use-account-timezone';
import { toPromotionDayString, fromStoredPromotionDay } from '@/lib/promotion-date';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { PromotionDetailsSkeleton } from '@/components/form/discount/promotion-details-skeleton';
import { PromotionForm, PromotionFormData } from '@/components/form/discount/promotion-form';
import { PromotionSummary } from '@/components/form/discount/promotion-summary';
import { PromotionFloatingActionBar } from '@/components/form/discount/promotion-floating-action-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { shouldWarnPromotionWithoutTrigger } from '@/lib/promotion-discount';
import { PromotionCodesDialog } from './promocode/components/promotion-codes-dialog';
import { DeletePromotionModal } from '../components/delete-promotion-modal';

type RuleType =
  | 'ContainsProduct'
  | 'OrderAmountMinimum'
  | 'OrderMinQuantity'
  | 'OrderMaxQuantity'
  | 'MaxProductQuantity'
  | 'PaymentIntegrationSelected'
  | 'DoNotContainRecurringProduct'
  | 'MaxAmount';

type ActionType =
  | 'LineItemAdjustment'
  | 'OrderAdjustment'
  | 'ShippingAdjustment'
  | 'CreateLineItem'
  | 'SubscriptionLineItemFreeTrial'
  | 'BOGO';

const buildUpdateData = (data: PromotionFormData, storeId: string) => ({
  storeId,
  name: data.name,
  enabled: data.enabled ?? false,
  automatic: data.automatic ?? false,
  usageLimit: data.usageLimit || undefined,
  startDate: data.startDate ? toPromotionDayString(data.startDate) : undefined,
  endDate: data.endDate ? toPromotionDayString(data.endDate) : undefined,
  ruleOperator: data.ruleOperator ?? 'AND',
  combinesWithOrderLevelDiscounts: data.combinesWithOrderLevelDiscounts ?? false,
  combinesWithLineItemDiscounts: data.combinesWithLineItemDiscounts ?? false,
  combinesWithShippingDiscounts: data.combinesWithShippingDiscounts ?? false,
  forceCombine: data.forceCombine ?? false,
  oncePerCustomer: data.oncePerCustomer ?? false,
  rules: data.rules.map((rule) => ({
    type: rule.type,
    productId: rule.productId,
    minimumQuantity: rule.minimumQuantity,
    minimumAmount: rule.minimumAmount,
    maximumAmount: rule.maximumAmount,
    variantIds: rule.variantIds,
    orderMinQuantity: rule.orderMinQuantity,
    orderMaxQuantity: rule.orderMaxQuantity,
    excludedProductIds: rule.excludedProductIds,
    maximumQuantity: rule.maximumQuantity,
    paymentMethodName: rule.paymentMethodName,
  })),
  actions: data.actions.map((action) => ({
    type: action.type,
    adjustmentType: action.adjustmentType,
    adjustmentPercentage: action.adjustmentPercentage,
    adjustmentAmount: action.adjustmentAmount,
    freeShipping: action.freeShipping,
    maxShippingAmount: action.maxShippingAmount,
    productIdToAdd: action.productIdToAdd,
    targetProductId: action.targetProductId,
    targetVariantIds: action.targetVariantIds,
    maxQuantityDiscounted: action.maxQuantityDiscounted,
    appliesOnEachItem: action.appliesOnEachItem,
    subscriptionFreeTrialDuration: action.subscriptionFreeTrialDuration,
    subscriptionFreeTrialDurationType: action.subscriptionFreeTrialDurationType,
    priceIdToAdd: action.priceIdToAdd,
    variantIdToAdd: action.variantIdToAdd,
    quantityToAdd: action.quantityToAdd,
    buyQuantityThreshold: action.buyQuantityThreshold,
  })),
});

const PromotionDetailsPageContainerInner = () => {
  const { promotionId: id } = useParams();
  const { id: storeId } = useStoreNavigate();
  const { t } = useTranslation('stores');

  if (!id) {
    throw new Error('Promotion ID is required');
  }
  const { data: promotion, isLoading, error } = usePromotion(id, storeId, !!id && !!storeId);
  const { isReady: isTimezoneReady } = useAccountTimezone();

  // Populating the date pickers depends on the account timezone; render a skeleton until it resolves
  // so a cold load never seeds the form with the 'UTC' fallback and shifts a stored day (TAG-1468).
  if (isLoading || !isTimezoneReady) {
    return <PromotionDetailsSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!promotion) {
    return (
      <div className="container mx-auto py-8">
        <p>{t('promotionNotFound')}</p>
      </div>
    );
  }
  return <PromotionDetailsPage promotion={promotion} storeId={storeId} />;
};

const PromotionDetailsPage = ({
  promotion,
  storeId,
}: {
  promotion: NonNullable<PromotionDetails>;
  storeId: string;
}) => {
  const { storeRoutes, navigate } = useStoreNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('stores');
  const { timezone } = useAccountTimezone();
  const updatePromotion = useUpdatePromotion();
  const [codesDialogOpen, setCodesDialogOpen] = React.useState(
    () => searchParams.get('tab') === 'promocode',
  );

  const initialData: PromotionFormData = {
    name: promotion.name,
    enabled: promotion.enabled ?? false,
    automatic: promotion.automatic ?? false,
    usageLimit: promotion.usageLimit || undefined,
    startDate: fromStoredPromotionDay(promotion.startDate, timezone),
    endDate: fromStoredPromotionDay(promotion.endDate, timezone),
    ruleOperator: (promotion as any).ruleOperator ?? 'AND',
    combinesWithOrderLevelDiscounts: promotion.combinesWithOrderLevelDiscounts ?? false,
    combinesWithLineItemDiscounts: promotion.combinesWithLineItemDiscounts ?? false,
    combinesWithShippingDiscounts: promotion.combinesWithShippingDiscounts ?? false,
    forceCombine: promotion.forceCombine ?? false,
    oncePerCustomer: (promotion as any).oncePerCustomer ?? false,
    rules:
      promotion.rules?.map((rule: any) => ({
        type: rule.type as RuleType,
        productId: rule.productId || undefined,
        productName: undefined,
        minimumQuantity: rule.minimumQuantity || undefined,
        minimumAmount: rule.minimumAmount || undefined,
        maximumAmount: rule.maximumAmount || undefined,
        variantIds: rule.variantIds || undefined,
        orderMinQuantity: rule.orderMinQuantity || undefined,
        orderMaxQuantity: rule.orderMaxQuantity || undefined,
        excludedProductIds: rule.excludedProductIds || undefined,
        maximumQuantity: rule.maximumQuantity || undefined,
        paymentMethodName: rule.paymentMethodName || undefined,
      })) || [],
    actions:
      promotion.actions?.map((action: any) => ({
        type: action.type as ActionType,
        adjustmentType: action.adjustmentType as 'percentage' | 'amount' | undefined,
        adjustmentPercentage: action.adjustmentPercentage || undefined,
        adjustmentAmount: action.adjustmentAmount || undefined,
        freeShipping: action.freeShipping || undefined,
        maxShippingAmount: action.maxShippingAmount || undefined,
        productIdToAdd: action.productIdToAdd || undefined,
        productNameToAdd: undefined,
        targetProductId: action.targetProductId || undefined,
        targetProductName: undefined,
        targetVariantIds: action.targetVariantIds || undefined,
        maxQuantityDiscounted: action.maxQuantityDiscounted || undefined,
        appliesOnEachItem: action.appliesOnEachItem || undefined,
        subscriptionFreeTrialDuration: action.subscriptionFreeTrialDuration || undefined,
        subscriptionFreeTrialDurationType: action.subscriptionFreeTrialDurationType || undefined,
        priceIdToAdd: action.priceIdToAdd || undefined,
        variantIdToAdd: action.variantIdToAdd || undefined,
        quantityToAdd: action.quantityToAdd || undefined,
        buyQuantityThreshold: action.buyQuantityThreshold || undefined,
      })) || [],
  };

  const handleSubmit = async (
    data: PromotionFormData,
    { reset }: { reset: (values?: PromotionFormData) => void },
  ) => {
    await updatePromotion.mutateAsync({
      id: promotion.id,
      data: buildUpdateData(data, storeId),
    } as any);
    reset(data);
    navigate(storeRoutes.DISCOUNTS());
  };

  const handleCancel = () => {
    navigate(storeRoutes.DISCOUNTS());
  };

  const handleCodesDialogChange = (open: boolean) => {
    setCodesDialogOpen(open);
    if (!open && searchParams.get('tab') === 'promocode') {
      const params = new URLSearchParams(searchParams);
      params.delete('tab');
      const search = params.toString();
      navigate({ search: search ? `?${search}` : '' }, { replace: true });
    }
  };

  return (
    <>
      {promotion.enabled && shouldWarnPromotionWithoutTrigger(promotion) && (
        <Alert variant="warning" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('promotion.noTrigger.title')}</AlertTitle>
          <AlertDescription>
            <p>{t('promotion.noTrigger.description')}</p>
            <Button variant="outline" size="sm" onClick={() => setCodesDialogOpen(true)}>
              <Ticket className="size-4" />
              {t('promotion.noTrigger.action')}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <PromotionForm
        BottomBar={(bar) => <PromotionFloatingActionBar mode="update" {...bar} />}
        Aside={() => (
          <PromotionAside
            promotion={promotion}
            persistedData={initialData}
            storeId={storeId}
            isUpdating={updatePromotion.isPending}
            onToggleEnabled={(nextEnabled) =>
              updatePromotion.mutateAsync({
                id: promotion.id,
                data: { ...buildUpdateData(initialData, storeId), enabled: nextEnabled },
              } as any)
            }
            onOpenCodes={() => setCodesDialogOpen(true)}
            onDeleted={() => navigate(storeRoutes.DISCOUNTS())}
          />
        )}
        mode="update"
        storeId={storeId}
        initialData={initialData}
        promotion={promotion}
        isPending={updatePromotion.isPending}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
      <PromotionCodesDialog
        open={codesDialogOpen}
        onOpenChange={handleCodesDialogChange}
        promotionId={promotion.id}
        storeId={storeId}
      />
    </>
  );
};

interface PromotionAsideProps {
  promotion: NonNullable<PromotionDetails>;
  persistedData: PromotionFormData;
  storeId: string;
  isUpdating: boolean;
  onToggleEnabled: (nextEnabled: boolean) => Promise<unknown>;
  onOpenCodes: () => void;
  onDeleted: () => void;
}

const PromotionAside = ({
  promotion,
  isUpdating,
  onToggleEnabled,
  onOpenCodes,
  onDeleted,
}: PromotionAsideProps) => {
  const { t } = useTranslation('stores');
  const { watch, setValue } = useFormContext<PromotionFormData>();
  const enabled = watch('enabled');
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const handleToggle = async () => {
    const next = !enabled;
    await onToggleEnabled(next);
    setValue('enabled', next, { shouldDirty: false });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onOpenCodes}>
          <Ticket className="size-4" />
          {t('promotion.codes.manageButton')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={isUpdating}>
              <MoreHorizontal className="size-4" />
              {t('promotion.actions.moreActions')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onSelect={() => void handleToggle()}>
              <Power className="size-4" />
              {enabled
                ? t('promotion.actions.disable')
                : t('promotion.actions.enable')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => setDeleteOpen(true)}>
              <Trash2 className="size-4" />
              {t('promotion.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <PromotionSummary promotion={promotion} />
      <DeletePromotionModal
        promotionId={deleteOpen ? promotion.id : undefined}
        onClose={() => setDeleteOpen(false)}
        onSuccess={onDeleted}
      />
    </div>
  );
};

export const PromotionDetailsPageContainer = withRequiredPermission(PromotionDetailsPageContainerInner, PERMISSIONS.STORES_VIEW);
